import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X, Upload, FolderPlus, Loader2, Pencil, Trash2, Maximize2, ChevronLeft,
  ChevronRight, Lock, Unlock, Settings2, Plus,
} from "lucide-react";
import { profileGalleryApi, streamProfileMedia } from "@/api/profileGallery";

function useMediaUrl(item) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    streamProfileMedia(item.id).then((u) => { url = u; setSrc(u); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [item.id]);
  return src;
}

function Tile({ item }) {
  const src = useMediaUrl(item);
  if (!src) return <div className="w-full h-full grid place-items-center bg-black"><Loader2 size={14} className="text-gold animate-spin" /></div>;
  return item.kind === "video"
    ? <video src={src} muted loop playsInline preload="metadata" className="w-full h-full object-cover"
        onMouseEnter={(e) => { const p = e.currentTarget.play(); if (p) p.catch(() => {}); }}
        onMouseLeave={(e) => e.currentTarget.pause()} onError={(e) => e.stopPropagation()} />
    : <img src={src} alt={item.filename} className="w-full h-full object-cover" onError={(e) => e.stopPropagation()} />;
}

function Viewer({ items, index, onIndex, onClose, albums, onAdd }) {
  const item = items[index];
  const src = useMediaUrl(item);
  return (
    <div className="absolute inset-0 z-[70] bg-black flex flex-col overflow-hidden" data-testid="pg-viewer">
      <div className="shrink-0 flex items-center justify-between px-3 py-2 border-b-2 border-gold/40">
        <span className="font-mono text-[10px] text-white/70 truncate">{item.filename} · {index + 1}/{items.length}</span>
        <button onClick={onClose} data-testid="pg-viewer-close" className="w-8 h-8 grid place-items-center border-2 border-gold bg-charcoal text-gold"><X size={16} /></button>
      </div>
      <div className="flex-1 min-h-0 relative grid place-items-center p-3">
        {!src ? <Loader2 size={26} className="text-gold animate-spin" /> : item.kind === "video"
          ? <video src={src} controls autoPlay loop playsInline className="max-w-full max-h-full object-contain" onError={(e) => e.stopPropagation()} />
          : <img src={src} alt={item.filename} className="max-w-full max-h-full object-contain" onError={(e) => e.stopPropagation()} />}
        {index > 0 && <button onClick={() => onIndex(index - 1)} data-testid="pg-viewer-prev" className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 grid place-items-center border-2 border-gold bg-charcoal text-gold rounded-full"><ChevronLeft size={18} /></button>}
        {index < items.length - 1 && <button onClick={() => onIndex(index + 1)} data-testid="pg-viewer-next" className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 grid place-items-center border-2 border-gold bg-charcoal text-gold rounded-full"><ChevronRight size={18} /></button>}
      </div>
      {albums.length > 0 && (
        <div className="shrink-0 border-t-2 border-gold/40 px-3 py-2">
          <p className="font-mono text-[8px] uppercase tracking-widest text-gold/70 mb-1">Add to album</p>
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
            {albums.map((a) => (
              <button key={a.id} onClick={() => onAdd(a, item)} data-testid={`pg-viewer-addalbum-${a.id}`} className="shrink-0 flex items-center gap-1 border-2 border-gold bg-charcoal text-gold font-mono text-[9px] font-black uppercase px-2 py-1 rounded"><FolderPlus size={11} /> {a.name}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="absolute inset-0 z-[80] bg-black/70 flex items-center justify-center p-5 overflow-hidden" data-testid={testid} onClick={onClose}>
      <div className="w-full bg-cream border-4 border-charcoal rounded-2xl p-4 shadow-[5px_5px_0px_#1A1A1A]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 pb-2 mb-3 border-b-2 border-charcoal/10">
          <h3 className="font-serif text-lg text-charcoal flex-1">{title}</h3>
          <button onClick={onClose} className="w-7 h-7 grid place-items-center border-2 border-charcoal bg-white rounded-lg"><X size={14} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Card({ item, onRename, onDelete, onOpen, testidPrefix }) {
  return (
    <div data-testid={`${testidPrefix}-${item.id}`}>
      <button onClick={onOpen} data-testid={`${testidPrefix}-open-${item.id}`} className="relative w-full aspect-square border-2 border-charcoal rounded-lg overflow-hidden shadow-[2px_2px_0px_#1A1A1A]">
        <Tile item={item} />
        <span className="absolute top-1 right-1 bg-charcoal/80 text-gold rounded-full w-5 h-5 grid place-items-center"><Maximize2 size={10} /></span>
      </button>
      <p className="mt-0.5 font-mono text-[8px] text-charcoal/60 truncate">{item.filename}</p>
      <div className="mt-0.5 flex gap-1">
        <button onClick={onRename} data-testid={`${testidPrefix}-rename-${item.id}`} className="flex-1 h-6 grid place-items-center border-2 border-charcoal bg-white rounded active:scale-95"><Pencil size={10} /></button>
        <button onClick={onDelete} data-testid={`${testidPrefix}-delete-${item.id}`} className="flex-1 h-6 grid place-items-center border-2 border-charcoal bg-red-500 text-white rounded active:scale-95"><Trash2 size={10} /></button>
      </div>
    </div>
  );
}

export default function MistGallery({ onClose }) {
  const [media, setMedia] = useState(null); // { images, videos }
  const [albums, setAlbums] = useState([]);
  const [tab, setTab] = useState("MEDIA"); // MEDIA | ALBUMS
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [albumOpen, setAlbumOpen] = useState(null);
  const [renameItem, setRenameItem] = useState(null);
  const [albumCfg, setAlbumCfg] = useState(null);
  const [draft, setDraft] = useState("");

  const reloadMedia = useCallback(() => {
    profileGalleryApi.media(0).then(setMedia).catch(() => setMedia({ images: [], videos: [] }));
  }, []);
  const reloadAlbums = useCallback(() => {
    profileGalleryApi.albums().then((d) => setAlbums(d.albums || [])).catch(() => setAlbums([]));
  }, []);
  useEffect(() => { reloadMedia(); reloadAlbums(); }, [reloadMedia, reloadAlbums]);

  const allMedia = media ? [...media.images, ...media.videos] : [];
  const byId = (id) => allMedia.find((m) => m.id === id);
  // IDs that live inside a PRIVATE album — hidden from the default grid, still resolvable for album viewing.
  const privateIds = new Set(albums.filter((a) => a.private).flatMap((a) => a.asset_ids || []));
  const publicMedia = allMedia.filter((m) => !privateIds.has(m.id));

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try { const r = await profileGalleryApi.upload(files); toast.success(`Uploaded ${r.uploaded.length} file(s)`); reloadMedia(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const newAlbum = async () => {
    try { await profileGalleryApi.createAlbum("New Album", false); toast.success("Album created"); reloadAlbums(); setTab("ALBUMS"); }
    catch { toast.error("Create failed"); }
  };
  const doRename = async () => {
    try { await profileGalleryApi.renameMedia(renameItem.id, draft.trim()); toast.success("Renamed"); setRenameItem(null); reloadMedia(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };
  const doDelete = async (item) => {
    if (!window.confirm(`Delete "${item.filename}"?`)) return;
    try { await profileGalleryApi.deleteMedia(item.id); toast.success("Deleted"); reloadMedia(); reloadAlbums(); }
    catch { toast.error("Delete failed"); }
  };
  const saveAlbumCfg = async () => {
    try { await profileGalleryApi.updateAlbum(albumCfg.id, { name: draft.trim(), private: albumCfg.private }); toast.success("Album updated"); setAlbumCfg(null); reloadAlbums(); reloadMedia(); }
    catch { toast.error("Update failed"); }
  };
  const delAlbum = async () => {
    try { await profileGalleryApi.deleteAlbum(albumCfg.id); toast("Album deleted"); setAlbumCfg(null); reloadAlbums(); reloadMedia(); }
    catch { toast.error("Delete failed"); }
  };
  const addToAlbum = async (album, item) => {
    try { await profileGalleryApi.updateAlbum(album.id, { add_asset: item.id }); toast.success(`Added to ${album.name}`); reloadAlbums(); reloadMedia(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="absolute inset-0 z-[60] bg-cream flex flex-col overflow-hidden" data-testid="mist-gallery">
      {/* Header — Mist Gallery + Upload / Album micro tabs */}
      <header className="shrink-0 flex items-center gap-2 px-4 pt-3 pb-2 border-b-2 border-charcoal">
        <button onClick={onClose} data-testid="pg-close" className="w-8 h-8 grid place-items-center border-2 border-charcoal bg-white rounded-full shadow-[2px_2px_0px_#1A1A1A]"><ChevronLeft size={16} /></button>
        <h2 className="flex-1 font-serif text-2xl text-charcoal">Mist Gallery</h2>
        <label data-testid="pg-upload-btn" className="flex items-center gap-1 bg-gold text-black border-2 border-charcoal rounded-full pl-2 pr-2.5 py-1.5 shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none cursor-pointer">
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} strokeWidth={2.6} />}
          <span className="font-mono text-[8px] font-black uppercase tracking-widest">Upload</span>
          <input type="file" hidden multiple accept="image/*,video/*" data-testid="pg-upload-input" onChange={onUpload} disabled={uploading} />
        </label>
        <button onClick={newAlbum} data-testid="pg-album-btn" className="flex items-center gap-1 bg-charcoal text-gold border-2 border-charcoal rounded-full pl-2 pr-2.5 py-1.5 active:translate-x-[1px] active:translate-y-[1px]">
          <FolderPlus size={13} strokeWidth={2.4} /><span className="font-mono text-[8px] font-black uppercase tracking-widest">Album</span>
        </button>
      </header>

      {/* Two half-screen rectangles meeting in the middle */}
      <div className="shrink-0 flex border-b-2 border-charcoal" data-testid="pg-tabs">
        <button onClick={() => setTab("MEDIA")} data-testid="pg-tab-media"
          className={`w-1/2 py-2.5 border-r-2 border-charcoal font-mono text-[11px] font-black uppercase tracking-[0.12em] ${tab === "MEDIA" ? "bg-charcoal text-gold" : "bg-cream text-charcoal/50"}`}>Images / Videos</button>
        <button onClick={() => setTab("ALBUMS")} data-testid="pg-tab-albums"
          className={`w-1/2 py-2.5 font-mono text-[11px] font-black uppercase tracking-[0.12em] ${tab === "ALBUMS" ? "bg-charcoal text-gold" : "bg-cream text-charcoal/50"}`}>Albums</button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar p-3">
        {tab === "MEDIA" ? (
          media == null ? <div className="grid place-items-center py-12"><Loader2 size={22} className="text-charcoal/40 animate-spin" /></div>
          : publicMedia.length === 0 ? (
            <p className="font-mono text-[11px] text-charcoal/50 text-center py-12">No uploads yet. Tap <b>Upload</b> to add images or videos.</p>
          ) : (
            <div className="grid grid-cols-3 gap-2" data-testid="pg-media-grid">
              {publicMedia.map((it, i) => (
                <Card key={it.id} item={it} testidPrefix="pg-card"
                  onOpen={() => setViewer({ items: publicMedia, index: i })}
                  onRename={() => { setRenameItem(it); setDraft(it.filename); }}
                  onDelete={() => doDelete(it)} />
              ))}
            </div>
          )
        ) : (
          albums.length === 0 ? (
            <p className="font-mono text-[11px] text-charcoal/50 text-center py-12">No albums yet. Tap <b>Album</b> to create one.</p>
          ) : (
            <div className="space-y-2" data-testid="pg-albums-list">
              {albums.map((a) => (
                <div key={a.id} className="flex items-center gap-2 border-2 border-charcoal bg-white rounded-xl p-2.5 shadow-[3px_3px_0px_#1A1A1A]" data-testid={`pg-album-${a.id}`}>
                  <span className={`w-8 h-8 grid place-items-center rounded-lg border-2 border-charcoal ${a.private ? "bg-rust text-white" : "bg-gold text-black"}`}>{a.private ? <Lock size={14} /> : <Unlock size={14} />}</span>
                  <button onClick={() => setAlbumOpen(a)} data-testid={`pg-album-open-${a.id}`} className="flex-1 text-left">
                    <p className="font-serif text-base text-charcoal leading-none">{a.name}</p>
                    <p className="font-mono text-[8px] uppercase tracking-widest text-charcoal/50 mt-1">{a.private ? "Private" : "Public"} · {a.asset_ids.length} items</p>
                  </button>
                  <button onClick={() => { setAlbumCfg(a); setDraft(a.name); }} data-testid={`pg-album-cfg-${a.id}`} className="w-8 h-8 grid place-items-center border-2 border-charcoal bg-white rounded-lg"><Settings2 size={14} /></button>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Album full-screen grid */}
      {albumOpen && (
        <div className="absolute inset-0 z-[65] bg-cream flex flex-col overflow-hidden" data-testid="pg-album-fullscreen">
          <div className="shrink-0 flex items-center gap-2 px-4 py-3 border-b-4 border-charcoal bg-white">
            <button onClick={() => setAlbumOpen(null)} data-testid="pg-album-back" className="w-9 h-9 grid place-items-center border-2 border-charcoal bg-white rounded-full shadow-[2px_2px_0px_#1A1A1A]"><ChevronLeft size={18} /></button>
            <div className="flex-1 leading-tight">
              <h3 className="font-serif text-xl text-charcoal flex items-center gap-1.5">{albumOpen.private && <Lock size={13} className="text-rust" />}{albumOpen.name}</h3>
              <p className="font-mono text-[8px] uppercase tracking-widest text-charcoal/50">{albumOpen.asset_ids.length} items</p>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar p-3">
            {albumOpen.asset_ids.length === 0 ? (
              <p className="font-mono text-[11px] text-charcoal/50 text-center py-10">Empty album. Open any media full-screen and tap "Add to album".</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {albumOpen.asset_ids.map((id) => byId(id)).filter(Boolean).map((it, i, arr) => (
                  <button key={it.id} onClick={() => setViewer({ items: arr, index: i })} data-testid={`pg-album-asset-${it.id}`} className="relative aspect-square border-2 border-charcoal rounded-lg overflow-hidden shadow-[2px_2px_0px_#1A1A1A]">
                    <Tile item={it} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {viewer && (
        <Viewer items={viewer.items} index={viewer.index}
          onIndex={(i) => setViewer((v) => ({ ...v, index: i }))}
          onClose={() => setViewer(null)} albums={albums} onAdd={addToAlbum} />
      )}

      {renameItem && (
        <Modal title="Rename" testid="pg-rename-modal" onClose={() => setRenameItem(null)}>
          <input value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="pg-rename-input" className="w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-xs mb-3" />
          <button onClick={doRename} data-testid="pg-rename-save" className="w-full bg-gold text-black border-2 border-charcoal rounded-xl py-2.5 font-mono text-[10px] font-black uppercase tracking-widest">Save</button>
        </Modal>
      )}

      {albumCfg && (
        <Modal title="Album settings" testid="pg-album-cfg-modal" onClose={() => setAlbumCfg(null)}>
          <input value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="pg-album-name-input" className="w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-xs mb-3" />
          <button onClick={() => setAlbumCfg((c) => ({ ...c, private: !c.private }))} data-testid="pg-album-private-toggle"
            className={`w-full flex items-center justify-center gap-2 border-2 border-charcoal rounded-xl py-2.5 font-mono text-[10px] font-black uppercase tracking-widest mb-2 ${albumCfg.private ? "bg-rust text-white" : "bg-white text-charcoal"}`}>
            {albumCfg.private ? <Lock size={13} /> : <Unlock size={13} />} {albumCfg.private ? "Private" : "Public"}
          </button>
          <div className="flex gap-2">
            <button onClick={saveAlbumCfg} data-testid="pg-album-save" className="flex-1 bg-gold text-black border-2 border-charcoal rounded-xl py-2.5 font-mono text-[10px] font-black uppercase tracking-widest">Save</button>
            <button onClick={delAlbum} data-testid="pg-album-delete" className="flex-1 bg-red-600 text-white border-2 border-charcoal rounded-xl py-2.5 font-mono text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-1"><Trash2 size={12} /> Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
